import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  X,
  ChevronDown,
  Download,
  Archive,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { DEFAULT_ADVANCED_TABLE_LABELS, type AdvancedTableLabels } from './advancedTableLabels';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdvancedActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onSetStatus: (disabled: 0 | 1) => Promise<void>;
  onExportCSV: () => void;
  onArchive: () => Promise<void>;
  onRestore?: () => Promise<void>;
  showRestore?: boolean;
  labels?: Partial<AdvancedTableLabels>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdvancedActionBar({
  selectedCount,
  onClearSelection,
  onSetStatus,
  onExportCSV,
  onArchive,
  onRestore,
  showRestore,
  labels,
}: AdvancedActionBarProps) {
  const l = { ...DEFAULT_ADVANCED_TABLE_LABELS, ...labels };
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const isVisible = selectedCount > 0;
  const isLoading = loadingAction !== null;

  const handleSetStatus = async (disabled: 0 | 1) => {
    setLoadingAction('status');
    try {
      await onSetStatus(disabled);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRestore = async () => {
    if (!onRestore) return;
    setLoadingAction('restore');
    try {
      await onRestore();
    } finally {
      setLoadingAction(null);
    }
  };

  const handleArchiveConfirm = async () => {
    setArchiveOpen(false);
    setLoadingAction('archive');
    try {
      await onArchive();
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <>
      {/* Floating action bar */}
      <div
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4 pointer-events-none"
        aria-hidden={!isVisible}
      >
        <div
          className={`
            pointer-events-auto flex h-14 items-center gap-3 rounded-md border
            bg-background/95 px-4 shadow-apple-lg backdrop-blur-md
            transition-transform motion-reduce:transition-none
            ${isVisible ? 'translate-y-0' : 'translate-y-[calc(100%+1rem)]'}
          `}
          style={{ transitionDuration: '150ms', transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
          role="toolbar"
          aria-label="Bulk actions"
        >
          {/* Selection count */}
          <span className="text-sm font-medium whitespace-nowrap">
            {selectedCount} {l.actionSelected}
          </span>

          {/* Separator */}
          <div className="h-6 w-px bg-border" />

          {/* Set Status dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1 text-xs"
                disabled={isLoading}
              >
                {loadingAction === 'status' && (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
                {l.actionSetStatus}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem onClick={() => handleSetStatus(0)}>
                {l.actionEnable}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSetStatus(1)}>
                {l.actionDisable}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Export CSV */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1 text-xs"
            disabled={isLoading}
            onClick={onExportCSV}
          >
            <Download className="h-3 w-3" />
            {l.actionExportCSV}
          </Button>

          {/* Restore (shown when viewing archived items) */}
          {showRestore && onRestore && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1 text-xs"
              disabled={isLoading}
              onClick={handleRestore}
            >
              {loadingAction === 'restore' && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              <RotateCcw className="h-3 w-3" />
              {l.actionRestore}
            </Button>
          )}

          {/* Archive (hidden when viewing archived items) */}
          {!showRestore && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1 text-xs"
              disabled={isLoading}
              onClick={() => setArchiveOpen(true)}
            >
              {loadingAction === 'archive' && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              <Archive className="h-3 w-3" />
              {l.actionArchive}
            </Button>
          )}

          {/* Separator */}
          <div className="h-6 w-px bg-border" />

          {/* Close */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClearSelection}
            disabled={isLoading}
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Archive confirmation dialog */}
      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {l.actionArchiveTitle} {selectedCount} {selectedCount === 1 ? 'item' : 'items'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {l.actionArchiveDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{l.actionCancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveConfirm}
            >
              {l.actionArchiveConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
